import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSession } from "../../hooks/useSession";
import { supabase } from "../../lib/supabaseClient";
import "./TermDiscussion.css";

/**
 * Drop-in discussion component for any term_submissions row.
 *
 * Props:
 * - termId (required): UUID of term_submissions.id
 * - title (optional): header title
 * - description (optional): small text under title
 * - allowPosting (optional): default true
 */
export default function TermDiscussion({
    termId,
    title = "Community Notes",
    description = "Player-submitted tips and insights.",
    allowPosting = true,
}) {
    const { session, loading: sessionLoading } = useSession();
    const user = session?.user ?? null;

    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");
    const [sortMode, setSortMode] = useState("top"); // "top" | "new"

    const [comments, setComments] = useState([]); // flat (non-deleted only)
    const [scoresById, setScoresById] = useState({});
    const [myVoteById, setMyVoteById] = useState({});
    const [profilesByUserId, setProfilesByUserId] = useState({});

    const [newText, setNewText] = useState("");
    const [posting, setPosting] = useState(false);

    // prevent stale requests from overwriting newer state
    const loadTokenRef = useRef(0);

    const loadAll = useCallback(async () => {
        if (!termId) return;

        const myToken = ++loadTokenRef.current;
        setMsg("");
        setLoading(true);

        try {
            // 1) Comments (ONLY visible)
            // Order by created_at ascending so "New" can be built without extra DB sort work.
            // We'll compute "Top" with score-based sort in-memory.
            const { data: cData, error: cErr } = await supabase
                .from("term_comments")
                .select("id, parent_id, user_id, body, created_at")
                .eq("term_submission_id", termId)
                .eq("is_deleted", false)
                .order("created_at", { ascending: true });

            if (cErr) throw cErr;
            if (loadTokenRef.current !== myToken) return;

            const list = cData ?? [];
            setComments(list);

            const commentIds = list.map((c) => c.id);
            const userIds = Array.from(new Set(list.map((c) => c.user_id)));

            // 2) Scores (batched)
            if (commentIds.length) {
                const { data: sData, error: sErr } = await supabase
                    .from("v_term_comment_scores")
                    .select("comment_id, score")
                    .in("comment_id", commentIds);

                if (sErr) throw sErr;
                if (loadTokenRef.current !== myToken) return;

                const map = {};
                (sData ?? []).forEach((r) => {
                    map[r.comment_id] = r.score ?? 0;
                });
                setScoresById(map);
            } else {
                setScoresById({});
            }

            // 3) My votes (batched)
            if (user && commentIds.length) {
                const { data: vData, error: vErr } = await supabase
                    .from("term_comment_votes")
                    .select("comment_id, value")
                    .eq("user_id", user.id)
                    .in("comment_id", commentIds);

                if (vErr) throw vErr;
                if (loadTokenRef.current !== myToken) return;

                const map = {};
                (vData ?? []).forEach((r) => {
                    map[r.comment_id] = r.value;
                });
                setMyVoteById(map);
            } else {
                setMyVoteById({});
            }

            // 4) Profiles (batched)
            if (userIds.length) {
                const { data: pData, error: pErr } = await supabase
                    .from("profiles")
                    .select("user_id, username, display_name, avatar_url")
                    .in("user_id", userIds);

                if (pErr) throw pErr;
                if (loadTokenRef.current !== myToken) return;

                const map = {};
                (pData ?? []).forEach((p) => {
                    map[p.user_id] = p;
                });
                setProfilesByUserId(map);
            } else {
                setProfilesByUserId({});
            }
        } catch (err) {
            if (loadTokenRef.current === myToken) {
                setMsg(err?.message || "Failed to load discussion.");
            }
        } finally {
            if (loadTokenRef.current === myToken) {
                setLoading(false);
            }
        }
    }, [termId, user]);

    useEffect(() => {
        if (sessionLoading) return;
        loadAll();
    }, [sessionLoading, loadAll]);

    // ----- Tree (deleted comments are not present, so no placeholders) -----
    const tree = useMemo(() => {
        // children map
        const children = new Map();
        for (const c of comments) {
            const pid = c.parent_id ?? null;
            if (!children.has(pid)) children.set(pid, []);
            children.get(pid).push(c);
        }

        const scoreOf = (id) => scoresById[id] ?? 0;

        // NOTE: comments are already created_at ascending from DB.
        // For "new" we want newest first => reverse at render time.
        // For "top" we want score desc then created_at desc.
        const sortList = (list) => {
            if (!list || list.length === 0) return [];

            if (sortMode === "new") {
                // newest first (DB gave ascending)
                return [...list].sort(
                    (a, b) =>
                        new Date(b.created_at).getTime() -
                        new Date(a.created_at).getTime(),
                );
            }

            // "top"
            return [...list].sort((a, b) => {
                const d = scoreOf(b.id) - scoreOf(a.id);
                if (d !== 0) return d;
                return (
                    new Date(b.created_at).getTime() -
                    new Date(a.created_at).getTime()
                );
            });
        };

        const build = (parentId) => {
            const list = children.get(parentId ?? null) ?? [];
            const sorted = sortList(list);
            return sorted.map((c) => ({
                comment: c,
                replies: build(c.id),
            }));
        };

        return build(null);
    }, [comments, scoresById, sortMode]);

    // ----- Actions -----
    const createComment = async ({ parentId = null, body }) => {
        setMsg("");
        if (!user) {
            setMsg("Log in to comment.");
            return;
        }

        const text = String(body ?? "").trim();
        if (!text) {
            setMsg("Comment can't be empty.");
            return;
        }

        setPosting(true);
        try {
            const { data, error } = await supabase
                .from("term_comments")
                .insert({
                    term_submission_id: termId,
                    parent_id: parentId,
                    user_id: user.id,
                    body: text,
                })
                .select("id, parent_id, user_id, body, created_at")
                .single();

            if (error) throw error;

            // Efficient: update local state (no full reload)
            if (data) {
                setComments((prev) => [...prev, data]);
            }

            if (!parentId) setNewText("");

            // Scores/votes for the new comment are unknown until next load
            // Keep it simple + correct: refresh scores/votes/profiles once
            await loadAll();
        } catch (err) {
            setMsg(err?.message || "Failed to post.");
        } finally {
            setPosting(false);
        }
    };

    // Deleted comments should disappear completely, so no "[deleted]" body update.
    const softDelete = async (commentId) => {
        setMsg("");
        if (!user) return;

        try {
            const { error } = await supabase
                .from("term_comments")
                .update({ is_deleted: true })
                .eq("id", commentId)
                .eq("user_id", user.id);

            if (error) throw error;

            // Efficient: remove locally
            setComments((prev) => prev.filter((c) => c.id !== commentId));
            // Also remove local score/vote entries
            setScoresById((prev) => {
                const next = { ...prev };
                delete next[commentId];
                return next;
            });
            setMyVoteById((prev) => {
                const next = { ...prev };
                delete next[commentId];
                return next;
            });
        } catch (err) {
            setMsg(err?.message || "Failed to delete.");
        }
    };

    const vote = async (commentId, nextValue) => {
        setMsg("");
        if (!user) {
            setMsg("Log in to vote.");
            return;
        }

        const current = myVoteById[commentId] ?? 0;

        try {
            if (current === nextValue) {
                const { error } = await supabase.rpc(
                    "clear_term_comment_vote",
                    {
                        p_comment_id: commentId,
                    },
                );
                if (error) throw error;

                // Optimistic local update
                setMyVoteById((prev) => ({ ...prev, [commentId]: 0 }));
                setScoresById((prev) => ({
                    ...prev,
                    [commentId]: (prev[commentId] ?? 0) - nextValue,
                }));
            } else {
                const { error } = await supabase.rpc("cast_term_comment_vote", {
                    p_comment_id: commentId,
                    p_value: nextValue,
                });
                if (error) throw error;

                // Optimistic local update (adjust score by delta)
                const delta = nextValue - (current || 0);
                setMyVoteById((prev) => ({ ...prev, [commentId]: nextValue }));
                setScoresById((prev) => ({
                    ...prev,
                    [commentId]: (prev[commentId] ?? 0) + delta,
                }));
            }
        } catch (err) {
            setMsg(err?.message || "Vote failed.");
            // If something went weird, reload as a correctness fallback
            await loadAll();
        }
    };

    if (!termId) return null;

    return (
        <div className="td-wrap">
            <div className="td-header">
                <div>
                    <h3 className="td-title">{title}</h3>
                    {description ? (
                        <p className="td-desc">{description}</p>
                    ) : null}
                </div>

                <div className="td-controls">
                    <button
                        type="button"
                        className={
                            "td-sort " + (sortMode === "top" ? "is-active" : "")
                        }
                        onClick={() => setSortMode("top")}
                    >
                        Top
                    </button>
                    <button
                        type="button"
                        className={
                            "td-sort " + (sortMode === "new" ? "is-active" : "")
                        }
                        onClick={() => setSortMode("new")}
                    >
                        New
                    </button>

                    <button
                        type="button"
                        className="td-refresh"
                        onClick={loadAll}
                        disabled={loading}
                    >
                        {loading ? "Loading..." : "Refresh"}
                    </button>
                </div>
            </div>

            {allowPosting ? (
                <div className="td-new">
                    {!user ? (
                        <div className="td-login-hint">
                            Log in to add a comment.
                        </div>
                    ) : (
                        <>
                            <textarea
                                className="td-textarea"
                                rows={3}
                                value={newText}
                                onChange={(e) => setNewText(e.target.value)}
                                placeholder="Add a comment..."
                                disabled={posting}
                            />
                            <div className="td-new-actions">
                                <button
                                    type="button"
                                    className="td-primary"
                                    onClick={() =>
                                        createComment({
                                            parentId: null,
                                            body: newText,
                                        })
                                    }
                                    disabled={posting}
                                >
                                    {posting ? "Posting..." : "Comment"}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            ) : null}

            {msg && <div className="td-msg">{msg}</div>}

            <div className="td-list">
                {tree.length === 0 ? (
                    <div className="td-empty">
                        No comments yet. Be the first.
                    </div>
                ) : (
                    tree.map((node) => (
                        <CommentNode
                            key={node.comment.id}
                            node={node}
                            user={user}
                            profilesByUserId={profilesByUserId}
                            scoresById={scoresById}
                            myVoteById={myVoteById}
                            onReply={createComment}
                            onDelete={softDelete}
                            onVote={vote}
                            depth={0}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

function CommentNode({
    node,
    user,
    profilesByUserId,
    scoresById,
    myVoteById,
    onReply,
    onDelete,
    onVote,
    depth,
}) {
    const c = node.comment;

    const score = scoresById[c.id] ?? 0;
    const myVote = myVoteById[c.id] ?? 0;

    const [replyOpen, setReplyOpen] = useState(false);
    const [replyText, setReplyText] = useState("");
    const [replying, setReplying] = useState(false);

    const p = profilesByUserId[c.user_id];
    const author =
        p?.display_name ||
        p?.username ||
        (c.user_id ? c.user_id.slice(0, 8) : "Unknown");
    const avatar = p?.avatar_url || "";

    const isOwner = user?.id && c.user_id === user.id;

    const submitReply = async () => {
        const body = replyText.trim();
        if (!body) return;

        setReplying(true);
        try {
            await onReply({ parentId: c.id, body });
            setReplyText("");
            setReplyOpen(false);
        } finally {
            setReplying(false);
        }
    };

    return (
        <div className="td-comment" style={{ marginLeft: depth * 14 }}>
            <div className="td-comment-main">
                <div className="td-votes">
                    <button
                        type="button"
                        className={"td-vote " + (myVote === 1 ? "is-on" : "")}
                        onClick={() => onVote(c.id, 1)}
                        aria-label="Upvote"
                        disabled={!user}
                    >
                        ▲
                    </button>
                    <div className="td-score">{score}</div>
                    <button
                        type="button"
                        className={"td-vote " + (myVote === -1 ? "is-on" : "")}
                        onClick={() => onVote(c.id, -1)}
                        aria-label="Downvote"
                        disabled={!user}
                    >
                        ▼
                    </button>
                </div>

                <div className="td-body">
                    <div className="td-meta">
                        {avatar ? (
                            <img
                                className="td-avatar"
                                src={avatar}
                                alt=""
                                loading="lazy"
                            />
                        ) : (
                            <div className="td-avatar td-avatar--empty" />
                        )}
                        <span className="td-author">{author}</span>
                        <span className="td-dot">•</span>
                        <span className="td-time">
                            {new Date(c.created_at).toLocaleString()}
                        </span>
                    </div>

                    <div className="td-text">{c.body}</div>

                    <div className="td-actions">
                        <button
                            type="button"
                            className="td-link"
                            onClick={() => setReplyOpen((v) => !v)}
                            disabled={!user}
                        >
                            Reply
                        </button>

                        {isOwner ? (
                            <>
                                <span className="td-dot">•</span>
                                <button
                                    type="button"
                                    className="td-link td-danger"
                                    onClick={() => onDelete(c.id)}
                                >
                                    Delete
                                </button>
                            </>
                        ) : null}
                    </div>

                    {replyOpen && user ? (
                        <div className="td-replybox">
                            <textarea
                                className="td-textarea"
                                rows={2}
                                value={replyText}
                                onChange={(e) => setReplyText(e.target.value)}
                                placeholder="Write a reply..."
                                disabled={replying}
                            />
                            <div className="td-new-actions">
                                <button
                                    type="button"
                                    className="td-primary"
                                    onClick={submitReply}
                                    disabled={replying}
                                >
                                    {replying ? "Replying..." : "Reply"}
                                </button>
                                <button
                                    type="button"
                                    className="td-secondary"
                                    onClick={() => setReplyOpen(false)}
                                    disabled={replying}
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>

            {node.replies?.length ? (
                <div className="td-replies">
                    {node.replies.map((child) => (
                        <CommentNode
                            key={child.comment.id}
                            node={child}
                            user={user}
                            profilesByUserId={profilesByUserId}
                            scoresById={scoresById}
                            myVoteById={myVoteById}
                            onReply={onReply}
                            onDelete={onDelete}
                            onVote={onVote}
                            depth={depth + 1}
                        />
                    ))}
                </div>
            ) : null}
        </div>
    );
}
